import { useMemo, useState } from "react";
import { Box, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import { MdOutlineAttachMoney } from "react-icons/md";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  BuscarVinculosEmpregadorParams,
  IVinculosEmpregadorRetorno,
  ordenarPorParaApi,
  recorteDoStatusFiltro,
  StatusFiltroRapido,
} from "@/models/consignado/proposal-billings.model";
import { buscarVinculosEmpregadores } from "@/services/consignado/proposta-cobranca.service";
import ComoUtilizarModal, {
  CONCEITOS_ESCRITURACAO_REPASSE,
  SLIDES_RELATORIOS_PARCELAS,
  SLIDE_GERACAO_BOLETOS,
} from "./ComoUtilizarModal";
import VinculosFiltersBar, { VinculosFiltrosDraft } from "./VinculosFiltersBar";
import ReprocessarCobrancasButton from "./ReprocessarCobrancasButton";
import StatusFilterBar from "./StatusFilterBar";
import EmpresaRow, { FiltrosCcbsEmpregador } from "./EmpresaRow";
import { EMPREGADOR_VINCULOS_QUERY_KEY, VINCULOS_EMPREGADORES_QUERY_KEY } from "./queryKeys";
import Pagination from "./Pagination";

const FILTROS_INICIAIS: VinculosFiltrosDraft = {
  tomador: "",
  empresa: "",
  ccb: "",
  ordenacao: "DESC",
  ordenarPor: "data_contratacao",
  statusNegociacao: "",
};

const PAGE_SIZE_INICIAL = 10;

// Texto do protótipo (feat/storybook VinculosAtivosRedesign). Grounded em
// portais e aplicações/ADM GT/CLT - consignado/cobrança/Cobrança (ADM GT).md —
// ações reais da tela (processar competência, disparar lembrete/aviso de
// atraso, boleto por parcela) e o significado de escriturado/repassado
// (eSocial → Caixa/instituição financeira).
const PASSOS_COMO_UTILIZAR = [
  {
    titulo: "Filtre por status",
    descricao:
      "Escriturados e Repassado mostram contratos com ao menos uma escrituração ou um repasse já concluído. Não escriturados e Não repassado mostram contratos com competência de prazo vencido em aberto (parcela paga por boleto não conta como repasse pendente). Contratos que ainda não venceram ficam só em Todos. Com um filtro ativo, o número de cada empresa conta só os contratos daquele filtro.",
  },
  {
    titulo: "Busque por tomador, empresa ou CCB",
    descricao:
      "Refine a lista com os campos de busca e ordene por data de contratação ou por competência em atraso. Na ordenação por competência vale a competência em atraso mais antiga de cada contrato (escrituração ou repasse vencido em aberto): as empresas sobem pela mais antiga entre os seus contratos, os contratos dentro da empresa seguem a mesma regra, e quem não tem atraso fica no fim. A competência aparece no card como \"Atraso desde\".",
  },
  {
    titulo: "Abra um vínculo",
    descricao:
      "Veja o resumo financeiro, a tabela de parcelas com escrituração e repasse, e o histórico de notificações de cada competência.",
  },
  {
    titulo: "Reprocessar",
    descricao:
      "No topo da tela, ao lado do título. Busca na bancarizadora as escriturações, repasses, términos e novos vínculos do período e reconcilia as competências de todas as CCBs, em segundo plano.",
  },
  {
    titulo: "Gere boletos",
    descricao:
      "Selecione as parcelas sem boleto ativo na tabela e gere a cobrança na bancarizadora (boleto e/ou PIX). Veja o passo a passo no slide \"Geração de boletos\".",
  },
  {
    titulo: "Relatórios de parcelas e quitação",
    descricao:
      "No cabeçalho da tabela de parcelas, exporte em PDF a situação das parcelas (por período) e o relatório de quitação (parcelas, data de cálculo e total a quitar). Veja os slides de cada relatório.",
  },
  {
    titulo: "Histórico de Vínculos",
    descricao:
      "Quando o vínculo ativo nasceu de uma renegociação, veja aqui o vínculo anterior encerrado que deu origem a ele.",
  },
  {
    titulo: "Contatos e negociações",
    descricao:
      "Cadastre contatos do tomador e do empregador, e registre negociações de cobrança por competência.",
  },
];

// Redesign da tela "Vínculos Ativos" (type ATIVOS). Nível 1 (empregadores) e
// nível 2 (CCBs por empregador) vêm de rotas reais; o detalhe (nível 3) tem
// query própria por seção — ver EmpresaRow / CcbDetalhe.
export default function VinculosAtivosRedesign() {
  const backgroundStyle = useConditionalBackground();
  const queryClient = useQueryClient();
  // Filtro rápido de status: REAL — vai como `recorte` na query do nível 1
  // (backend `3da2c200`); quem classifica escriturado/repassado é o backend
  // ([[no-front-calculations]]). Aplica na hora, sem passar pelo "Filtrar".
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltroRapido>("TODOS");
  const [filtrosDraft, setFiltrosDraft] = useState<VinculosFiltrosDraft>(FILTROS_INICIAIS);
  const [filtrosAplicados, setFiltrosAplicados] = useState<VinculosFiltrosDraft>(FILTROS_INICIAIS);
  const [pagina, setPagina] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_INICIAL);

  const params: BuscarVinculosEmpregadorParams = useMemo(
    () => ({
      pagina,
      limite: pageSize,
      order: filtrosAplicados.ordenacao,
      ordenar_por: ordenarPorParaApi(filtrosAplicados.ordenarPor),
      tomador: filtrosAplicados.tomador || undefined,
      empresa: filtrosAplicados.empresa || undefined,
      codigo_proposta: filtrosAplicados.ccb || undefined,
      recorte: statusFiltro === "TODOS" ? undefined : recorteDoStatusFiltro(statusFiltro),
      status_negociacao: filtrosAplicados.statusNegociacao || undefined,
    }),
    [pagina, pageSize, filtrosAplicados, statusFiltro]
  );

  // O nível 2 recebe os mesmos filtros (menos `empresa`, que o CNPJ já resolve)
  // pra listar só as CCBs que contaram no card.
  const filtrosCcbs: FiltrosCcbsEmpregador = useMemo(
    () => ({
      order: params.order,
      ordenar_por: params.ordenar_por,
      tomador: params.tomador,
      codigo_proposta: params.codigo_proposta,
      recorte: params.recorte,
      status_negociacao: params.status_negociacao,
    }),
    [params]
  );

  const { data, isLoading, isError, isFetching } = useQuery<IVinculosEmpregadorRetorno>({
    queryKey: [VINCULOS_EMPREGADORES_QUERY_KEY, params],
    queryFn: () => buscarVinculosEmpregadores(params),
    placeholderData: (prev) => prev,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const handleFiltrar = () => {
    setFiltrosAplicados(filtrosDraft);
    setPagina(1);
  };
  const handleLimparFiltros = () => {
    setFiltrosDraft(FILTROS_INICIAIS);
    setFiltrosAplicados(FILTROS_INICIAIS);
    setStatusFiltro("TODOS");
    setPagina(1);
  };

  // Reprocessamento aceito: o job roda em background, então só invalida os
  // dois níveis da listagem pra refetch pegar o status novo quando chegar.
  const handleReprocessado = () => {
    queryClient.invalidateQueries({ queryKey: [VINCULOS_EMPREGADORES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [EMPREGADOR_VINCULOS_QUERY_KEY] });
  };
  const handleSetStatusFiltro = (status: StatusFiltroRapido) => {
    setStatusFiltro(status);
    setPagina(1);
  };
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPagina(1);
  };

  const empregadores = data?.data ?? [];

  return (
    <Box w="100%" padding={{ base: "2", md: "4" }}>
      <Flex flexDirection="column" alignItems="center" width="100%">
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: "4", md: "6" }}
          color="white"
          boxShadow="md"
          width="100%"
          mb={6}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            flexWrap="wrap"
            mb={{ base: "4", md: "6" }}
          >
            <Flex alignItems="center" gap={3} flexWrap="wrap">
              <Flex alignItems="center">
                <MdOutlineAttachMoney size="2rem" />
                <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="bold" ml={2} fontFamily="Lexend Deca">
                  Vinculos Ativos
                </Text>
              </Flex>
              <ReprocessarCobrancasButton onReprocessado={handleReprocessado} />
            </Flex>
            <ComoUtilizarModal
              tituloTela="Vínculos Ativos"
              passos={PASSOS_COMO_UTILIZAR}
              conceitos={CONCEITOS_ESCRITURACAO_REPASSE}
              slidesExtras={[...SLIDES_RELATORIOS_PARCELAS, SLIDE_GERACAO_BOLETOS]}
            />
          </Flex>

          <VStack align="stretch" spacing={4}>
            <StatusFilterBar
              statusFiltro={statusFiltro}
              setStatusFiltro={handleSetStatusFiltro}
            />
            <VinculosFiltersBar
              filtrosDraft={filtrosDraft}
              setFiltrosDraft={setFiltrosDraft}
              onFiltrar={handleFiltrar}
              onLimparFiltros={handleLimparFiltros}
              mostrarOrdenacaoCompetencia
              mostrarFiltroStatusNegociacao
            />
          </VStack>
        </Box>

        {isLoading && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Spinner size="lg" color="primary" />
          </Flex>
        )}

        {isError && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Text fontSize="xl" fontFamily="Lexend Deca" color="gray.400" textAlign="center">
              Não foi possível carregar os empregadores.
            </Text>
          </Flex>
        )}

        {!isLoading && !isError && empregadores.length === 0 && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Text fontSize="xl" fontFamily="Lexend Deca" color="gray.400" textAlign="center">
              Nenhum empregador encontrado
            </Text>
          </Flex>
        )}

        {!isLoading && !isError && empregadores.length > 0 && (
          <>
            <Pagination
              currentPage={data?.pagina ?? 1}
              totalPages={data?.total_paginas ?? 1}
              pageSize={pageSize}
              totalItems={data?.total ?? empregadores.length}
              onPageChange={setPagina}
              onPageSizeChange={handlePageSizeChange}
            />

            <VStack align="stretch" spacing={5} width="100%" opacity={isFetching ? 0.6 : 1}>
              {empregadores.map((empregador) => (
                <EmpresaRow key={empregador.cnpj} empregador={empregador} filtros={filtrosCcbs} />
              ))}
            </VStack>

            <Pagination
              currentPage={data?.pagina ?? 1}
              totalPages={data?.total_paginas ?? 1}
              pageSize={pageSize}
              totalItems={data?.total ?? empregadores.length}
              onPageChange={setPagina}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </Flex>
    </Box>
  );
}
